import React, { useEffect, useState } from 'react';
import { StaticContext } from 'react-router';
import { RouteComponentProps, Link } from 'react-router-dom';
import { Redirect } from 'react-router-dom';
import { Theme, Typography, Box } from '@mui/material';
import { WithStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Spinner from 'components/Spinner';
import { Routes } from 'routing';
import { useTranslation } from 'services/localization/useTranslation';
import { newLoginEvent } from 'common/services/analytics';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';
import * as UserActions from 'actions/UserActions';
import { stringEnum } from 'common/utils/objects';
import { EnumValues } from 'common/types';
import errorHandler from 'common/services/errorHandler';
import { signInWithEmailAndPassword } from 'common/frontend/firebase/auth';
import AuthContainer from 'components/AuthContainer';

type LocationState = {
	from: { [key: string]: string };
};

type Props = RouteComponentProps<{}, StaticContext, LocationState> & WithStyles<typeof styles>;

type InputEvent = React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement | HTMLSelectElement>;

const InvalidUserCodes = stringEnum([
	'auth/invalid-email',
	'auth/user-disabled',
	'auth/user-not-found',
]);

const InvalidPasswordCodes = stringEnum(['auth/wrong-password']);

const AuthErrorCodes = { ...InvalidUserCodes, ...InvalidPasswordCodes };

type AuthErrorCode = EnumValues<typeof AuthErrorCodes>;

const Login = (props: Props) => {
	const dispatch = useDispatch();
	const [email, setEmail] = useState<string>('');
	const [password, setPassword] = useState<string>('');
	const [error, setError] = useState<{ message: string; additionalInfo?: string } | null>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [redirectAfterLogin, setRedirectAfterLogin] = useState<boolean>(false);

	const classes = props.classes;
	const { t } = useTranslation();

	useEffect(() => {
		dispatch(UserActions.logout());
	}, [dispatch]);

	const handleEmailChange = (event: InputEvent) => {
		const value = event.target.value;
		setEmail(value);
	};

	const handlePasswordChange = (event: InputEvent) => {
		const value = event.target.value;
		setPassword(value);
	};

	const login = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setError(null);
		setLoading(true);
		signInWithEmailAndPassword(email, password)
			.then((result) => {
				setError(null);
				setPassword('');
				setEmail('');
				setLoading(false);
				setRedirectAfterLogin(true);
				newLoginEvent({ method: 'email' });
			})
			.catch((err: { code: AuthErrorCode; message: string }) => {
				let errorMessage = t('common:errors.errorMessage');
				if (!!InvalidUserCodes[err.code] || !!InvalidPasswordCodes[err.code]) {
					errorMessage = t('login.invalidEmailOrPassword', 'Incorrect email or password');
				}
				errorHandler.report(err.code);
				setError({
					message: errorMessage,
				});
				setLoading(false);
			});
	};

	const { from } = (props.location && props.location.state) || {
		from: { pathname: Routes.ROOT },
	};

	if (redirectAfterLogin) {
		return <Redirect to={from} />;
	}

	return (
		<AuthContainer title={t('common:actions.login', 'Log in')}>
			<form onSubmit={login}>
				<TextField
					id="email"
					variant="outlined"
					label={t('common:form.email')}
					name="email"
					autoComplete="email"
					className={classes.textField}
					value={email}
					onChange={handleEmailChange}
					margin="normal"
					type="text"
					required
				/>
				<TextField
					id="password"
					variant="outlined"
					label={t('common:form.password', 'Password')}
					name="password"
					autoComplete="current-password"
					className={classes.textField}
					value={password}
					onChange={handlePasswordChange}
					margin="normal"
					type="password"
					required
				/>
				{!!error && (
					<Box my={2} display="block">
						<Typography color="error" variant="body2" gutterBottom>
							{error.message}
						</Typography>
						{!!error.additionalInfo && (
							<Typography color="error" variant="caption">
								{error.additionalInfo}
							</Typography>
						)}
					</Box>
				)}
				<Link to={Routes.PASSWORD_RESET + `?email=${encodeURIComponent(email)}`}>
					<Typography variant="caption" className={classes.noTextTransform}>
						{t('common:form.forgotPassword', 'Forgot your password?')}
					</Typography>
				</Link>
				<Box my={4}>
					<Button
						className={classes.loginBtn}
						variant="contained"
						type="submit"
						disabled={loading}
						data-qa="login"
					>
						{loading ? <Spinner padding={8} thickness={4} /> : t('common:actions.login', 'Log in')}
					</Button>
				</Box>
			</form>
			<Typography className={classes.notRegistered} variant="body2">
				{t('login.notRegistered', 'Not registered yet?')}{' '}
				<Link
					className={classNames(classes.notRegistered, classes.linkUnderlined)}
					to={Routes.REGISTER}
				>
					{t('login.register', 'Create a new user')}
				</Link>
			</Typography>
		</AuthContainer>
	);
};

const styles = (theme: Theme) =>
	createStyles({
		loginBtn: {
			width: '100%',
			background: theme.palette.common.black,
			'&:hover': {
				background: theme.palette.primary.dark,
			},
		},
		textField: {
			marginLeft: 'auto',
			marginRight: 'auto',
			display: 'flex',
		},
		noTextTransform: {
			textTransform: 'none',
			color: theme.palette.primary.main,
		},
		linkUnderlined: {
			color: theme.palette.primary.main,
			textDecoration: 'underline',
		},
		notRegistered: {
			width: 'fit-content',
		},
	});

export default withStyles(styles)(Login);
